import React from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import type { CalendarAvailabilitySlot } from '../../../types/api-responses';
import { getRejectionUx } from '../rejectionMessages';
import s from '../QuickBookModal.module.css';

interface SlotPickerGridProps {
    slots: CalendarAvailabilitySlot[];
    isClosestMatch: boolean;
    around?: string | null;
    selectedSlotKey: string | null;
    onSelect: (slot: CalendarAvailabilitySlot) => void;
    /**
     * When the modal is in staff-locked mode (right-click "Book with X" or
     * click-to-create on a staff column), the empty-state CTA shouldn't
     * suggest "try a different staff" — changing staff defeats the
     * purpose. Pass the locked name so the empty state reframes the
     * suggestion around dates only.
     */
    lockedStaffName?: string;
    /**
     * Audit Phase A — full BE rejection code that triggered the lock
     * release (or null if the lock is still held / no rejection occurred
     * yet). The picker hands this straight to `getRejectionUx` so the
     * banner + empty state both source their copy from the same matrix
     * as the toast at the top of the modal. Previously this prop was a
     * narrowed parsed reason (`'STAFF_CANT_SERVICE' | ...`) which
     * required updating the type every time BE added a code; now any
     * string passes through and unknown codes get a generic fallback.
     */
    lockReleaseCode?: string | null;
    /** The name of the staff that was originally locked, for the message. */
    releasedFromStaffName?: string;
    /** The service name, for the message. */
    serviceName?: string;
    /** The branch name, for the message. */
    branchName?: string;
}

/**
 * Initial-pair avatar text from a full name.
 */
function initials(name: string): string {
    if (!name) return '?';
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p.charAt(0).toUpperCase())
        .join('');
}

/**
 * AB#1751 — Quick-book slot picker, mockup §C row pattern.
 *
 * Each slot is a single row with three logical sections (CSS grid):
 *   ┌─────────┬──────────────────────────────────┬──────────────┐
 *   │  TIME   │  AVATAR · staffName · genderGlyph│ → endTime    │
 *   │ 13:00   │  SA · Sara · ♀                   │ → 13:30 Match│
 *   └─────────┴──────────────────────────────────┴──────────────┘
 *
 * The first slot is the "closest match" candidate — when the availability
 * engine had to widen its window to find anything, the BE flags the whole
 * result set with `isClosestMatch=true` and the first row gets a "Match"
 * badge to draw the owner's eye to the recommended candidate.
 */
const SlotPickerGrid: React.FC<SlotPickerGridProps> = ({
    slots, isClosestMatch, around, selectedSlotKey, onSelect, lockedStaffName,
    lockReleaseCode, releasedFromStaffName, serviceName, branchName,
}) => {
    const { t } = useLanguage();
    if (slots.length === 0) {
        // Audit Phase A — Empty-state copy flows through getRejectionUx
        // so the picker matches whichever toast just fired. Three priority
        // levels:
        //
        //   1. lockReleaseCode set → use the matrix helper. This handles
        //      STAFF_CANT_SERVICE / GENDER_MISMATCH / OUTSIDE_SHIFT /
        //      STAFF_NOT_AT_BRANCH and any future BE code uniformly.
        //   2. lockedStaffName set (still locked) → terse "no slots for
        //      X" with a date suggestion. Used when the user opened
        //      the modal via right-click "Book with X" or a Day-view
        //      column click that hasn't been rejected yet.
        //   3. Neither → generic fallback. Means the user reached the
        //      picker without any prefill or rejection — e.g., header
        //      QuickBookLauncher → pick category/service → land here.
        if (lockReleaseCode && releasedFromStaffName) {
            const ux = getRejectionUx(lockReleaseCode, {
                staffName: releasedFromStaffName,
                serviceName,
                branchName,
                around,
            }, t);
            return (
                <div className={s.placeholderState}>
                    <strong>{ux.emptyTitle}</strong>
                    <div className={s.placeholderHint}>{ux.emptyHint}</div>
                </div>
            );
        }
        if (lockedStaffName) {
            return (
                <div className={s.placeholderState}>
                    {t('cal.qb.noSlotsStaff').replace('{name}', lockedStaffName)}
                </div>
            );
        }
        return (
            <div className={s.placeholderState}>
                {t('cal.qb.noSlots')}
            </div>
        );
    }

    return (
        <>
            {isClosestMatch && around && (
                <div className={s.closestBanner} role="status">
                    ⚠ {t('cal.qb.closestMatch').replace('{time}', around)}
                </div>
            )}
            <div className={s.slotList} role="listbox" aria-label={t('cal.qb.availableSlots')}>
                {slots.map((slot, idx) => {
                    const key = `${slot.staffId}_${slot.startTime}`;
                    const selected = selectedSlotKey === key;
                    // First row gets the "Match" badge to call out the
                    // closest candidate per mockup §C lines 1441-1448.
                    const isClosest = idx === 0;
                    return (
                        <button
                            key={key}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className={`${s.slotRow} ${selected ? s.slotRowSelected : ''} ${isClosest ? s.slotRowClosest : ''}`}
                            onClick={() => onSelect(slot)}
                        >
                            <span className={s.slotRowTime}>{slot.startTime}</span>
                            <span className={s.slotRowStaff}>
                                <span className={s.slotRowAvatar}>{initials(slot.staffDisplayName)}</span>
                                <span className={s.slotRowName}>
                                    {slot.staffDisplayName}
                                    {slot.staffGender === 'Female' && (
                                        <span className={`${s.slotGenderGlyph} ${s.slotGenderF}`} aria-label={t('cal.qb.female')}>♀</span>
                                    )}
                                    {slot.staffGender === 'Male' && (
                                        <span className={`${s.slotGenderGlyph} ${s.slotGenderM}`} aria-label={t('cal.qb.male')}>♂</span>
                                    )}
                                </span>
                            </span>
                            <span className={s.slotRowEnd}>
                                → {slot.endTime}
                                {isClosest && <span className={s.slotClosestBadge}>{t('cal.qb.match')}</span>}
                            </span>
                        </button>
                    );
                })}
                <div className={s.slotListFooter}>
                    Showing {slots.length} nearest · scroll for more
                </div>
            </div>
        </>
    );
};

export default SlotPickerGrid;
